import React from 'react';

import {assert} from 'Asserts';
import isModelTypeEnabledForListing from 'util/isModelTypeEnabledForListing';

import {getFilterKeysForModelType, getQuickFilterKeysForModelType} from '../FiltersByType';
import * as FilterUtil from '../FilterUtil';
import {Filter, FilterKey, MultiValueFilter, QuickFilterKey} from '../SearchConstants';
import FiltersContainer, {FiltersContainerProps} from './FiltersContainer';
import {sanitizeTypes} from './ValidateFilters';

function narrowFilterKeysToTypes(filterKeys: Array<FilterKey>, types: Array<string>): Array<FilterKey> {
  return filterKeys.filter((key) => types.some((type) => getFilterKeysForModelType(type).includes(key)));
}

/**
 * A light wrapper around FiltersContainer, to use specifically in listings.
 *
 * Expects all parent components to always have a TYPES filter set.
 */
export default class ListingFiltersContainer extends React.Component<FiltersContainerProps> {
  private getSelectedTypes = (): Array<string> | null => {
    const typesFilter = FilterUtil.getFilterByKey(
      this.props.filters,
      FilterKey.TYPES
    ) as MultiValueFilter | null;
    if (typesFilter == null) {
      return null;
    }
    const types = typesFilter.value.filter((type) => isModelTypeEnabledForListing(type));
    return types;
  };

  private filterAvailableKeys = (filterKeys: Array<FilterKey>): Array<FilterKey> => {
    const types = this.getSelectedTypes();
    assert(types != null, 'Expected TYPES filter to exist for listing.');
    return narrowFilterKeysToTypes(filterKeys, types);
  };

  private filterAvailableQuickFilters = (quickFilterKeys: Array<QuickFilterKey>): Array<QuickFilterKey> => {
    const types = this.getSelectedTypes() ?? [];
    return quickFilterKeys.filter((quickFilterKey) =>
      types.some((type) => getQuickFilterKeysForModelType(type).includes(quickFilterKey))
    );
  };

  private handleChangeFilters = (filters: Array<Filter>): void => {
    // If the types filter did not change, narrow the types to the types that support the filter.
    // Do not do this if the types filter changed, since that may invalidate some of the filters.
    if (
      FilterUtil.getFilterValueByKey(this.props.filters, FilterKey.TYPES) ===
      FilterUtil.getFilterValueByKey(filters, FilterKey.TYPES)
    ) {
      const {filters: newFilters} = sanitizeTypes(filters, this.props.selectedSchema);
      this.props.onChangeFilters(newFilters);
    } else {
      this.props.onChangeFilters(filters);
    }
  };

  render(): JSX.Element {
    return (
      <FiltersContainer
        {...this.props}
        filterAvailableKeys={this.filterAvailableKeys}
        filterAvailableQuickFilters={this.filterAvailableQuickFilters}
        onChangeFilters={this.handleChangeFilters}
      />
    );
  }
}
